import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { get } from 'lodash-es';

import { MultiBrandPassService } from '@core/services/multi-brand-pass/multi-brand-pass.service';
import { LocalStorageService } from '../util/local-storage.service';
import { SubscriptionPriceService } from '@core/services/subscription-price/subscription-price.service';
import { SessionStorageService } from '@core/services//util/session-storage.service';
import { StorageService } from '../util/storage.service';
import { ApplicableOfferDetailsAPIResponse, PurchasedPass } from '@app/shared/models/api-response/pass-api-response';

interface PassModel {
    has_pass: boolean;
    price: number | null;
    is_applied: boolean;
}
@Injectable({
    providedIn: 'root'
})
export class PassService {
    menu: any;

    // individually mainting both passes, will check both when ask for active pass
    cartPass: any;
    passModel: Record<number, PassModel>;

    customerPassDetails$ = new BehaviorSubject<ApplicableOfferDetailsAPIResponse | {}>({});
    passModel$: any = new BehaviorSubject<any>({});
    customerPass: PurchasedPass;

    constructor(
        private multiBrandPassService: MultiBrandPassService,
        private localStorageService: LocalStorageService,
        private sessionStorage: SessionStorageService,
        private storageService: StorageService
    ) {}

    setMenu(menu) {
        this.menu = menu;
        const cart =
            this.localStorageService.get('cart') ||
            Object.create(null, { items: { value: Object.create(null), enumerable: true } });
        this.updateSubscriptionPrices();
        this.setCartPass(cart);
    }

    generatePassModel() {
        if (!this.menu || !Object.keys(this.menu).length) {
            return false;
        }

        const activePass = this.getActivePass();
        const { products } = this.menu;
        this.passModel = {};
        Object.values(products).filter((product: any) => {
            const size = this.getDefaultSize(product.product_sizes);
            const subscriptions = get(size, 'prices.subscription_prices', []);
            const pass_ids = subscriptions.map(subscription => subscription.pass_id);
            this.passModel[product.id] = {
                has_pass: Boolean(pass_ids.length),
                price: get(subscriptions, '[0].price', null),
                is_applied: Boolean(activePass)
            };
        });
        this.passModel$.next(this.passModel);
    }

    getDefaultSize(sizes) {
        if (!sizes) {
            return null;
        }

        const defaultSize = sizes.find(size => size.dflt);

        if (defaultSize) {
            return defaultSize;
        }

        for (const size of sizes) {
            if (!size.sold_out) {
                return size;
            }
        }

        return sizes[0];
    }

    setCartPass(cart) {
        this.cartPass = null;
        Object.values(cart.items).forEach((item: any) => {
            if (item.is_subscription_item) {
                const passDetails = this.sessionStorage.get('passes');
                this.cartPass = passDetails?.all_passes.filter(pass => item.ids.product_size_id === pass.product_size_id)[0];
            }
        });
        this.generatePassModel();
    }

    updateSubscriptionPrices() {
        const customerPassDetails = this.sessionStorage.get('passes');
        if (customerPassDetails && Object.keys(customerPassDetails)) {
            const subscriptionService = new SubscriptionPriceService(customerPassDetails, this.menu);
            subscriptionService.applyPassPrices();
        }
    }

    initCustomerPasses() {
        return new Promise(resolve => {
            this.multiBrandPassService
                .getPassInfo()
                .then(response => {
                    if (response.meta.code === 200) {
                        this.setPurchasedPass(response);
                        resolve(true);
                    }
                })
                .catch(err => {
                    console.log(err);
                });
        });
    }

    resetCustomerPasses() {
        this.customerPass = null;
        this.sessionStorage.remove('passes');
    }

    getActivePass() {
        // priority will be given to pass added in cart over current subscription
        return this.cartPass || this.customerPass || null;
    }

    setPurchasedPass(response: ApplicableOfferDetailsAPIResponse) {
        const { purchased_passes } = response;
        const serviceType = this.storageService.get('service_type');
        response.all_passes = response.all_passes.filter(pass => pass.service_type_codes.includes(serviceType));
        this.customerPass = purchased_passes[0] || null;
        this.sessionStorage.set('passes', response);
        this.customerPassDetails$.next(response);
    }

    getProductPassModel(productId) {
        return this.passModel[productId];
    }
}
